import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';
import arrayMutators from 'final-form-arrays';
import React, { useCallback, useEffect } from 'react';
import { Form } from 'react-final-form';
import { Helmet } from 'react-helmet';
import { ConnectedProps, connect } from 'react-redux';
import { redirect, useNavigate } from 'react-router';

import { gengql } from '@/__generated__';
import { ExpertRequestStage } from '@/__generated__/graphql';
import { setAddress } from '@/actions/address';
import { fetchProject, invalidateFetchedProjectsCache, saveProject } from '@/actions/project';
import { notify, popup } from '@/actions/ui';
import Body from '@/components/Body';
import Layout from '@/components/Layout';
import MediaQuery from '@/components/MediaQuery';
import TeamAccountPromo from '@/components/TeamAccountPromo/TeamAccountPromo';
import { segmentTracking } from '@/core/analytics';
import { apiErrorMessage, apiErrorMessages, firstErrorCode, hasErrorCode } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';
import { fetchExpertRequest, saveExpertRequest } from '@/expertrequest/store';
import NotFoundPage from '@/pages/NotFoundPage';
import { LegacyRoute } from '@/routes/routesMiddleware';
import { RootState } from '@/store';
import { SCREEN_SM } from '@/theme/screens';
import { clearCache, getCache, queryPart, setCache } from '@/utils';

import AddMembers from './AddMembers';
import Details from './Details';
import ExpertRequestSpy from './ExpertRequestSpy';
import { ReadyToSourceModal } from './ReadyToSourceModal';
import SelectType from './SelectType';
import { copyExpertRequest, getDefaultValues } from './util';

const baseUrl = '/request_expert';
const cacheValueKey = 'newExpertRequest';

const GET_EXPERT_REQUEST = gengql(/* GraphQL */ `
  query expertRequestNewGetExpertRequest($id: String!) {
    expertRequest(id: $id) {
      id
      name
      er_type
      description
      focus_areas
      qualifications {
        id
        query
        response_type
        required
      }
      questions {
        id
        query
        response_type
        required
      }
      attachments {
        id
        created_at
        author {
          id
          name
          html_url
        }
        expert_request_id
        consultation_id
        name
        description
        file_url
        hide_from_experts
      }
      companies_pursue
      companies_avoid
      disclosure
      regions {
        id
        name
      }
      sectors {
        id
        name
      }
      tags
      expected_duration
      origin
      sourcing_scope
      allow_internal_vetting
      allow_external_recruiting
      project {
        id
      }
      phone
    }
  }
`);

type Component = React.FC<Record<string, any>> & {
  title: string;
  fetch?: unknown | undefined;
  validate?: (values: any, props: any) => any | undefined;
};

type Step = {
  path: string;
  component: Component;
  LayoutParams?: Record<string, any>;
};

const steps: Step[] = [
  {
    path: 'type',
    component: SelectType,
  },
  {
    path: 'details',
    component: Details as Component,
    LayoutParams: {
      showNav: true,
    },
  },
];

const route: LegacyRoute = {
  path: `${baseUrl}/:stepPath?`,

  async action({ store, params, query }) {
    const { viewer } = store.getState();
    const projectId = query.get('project_id');

    // R-8: SMB self-service users mid-flow have a group (auto-created at
    // signup) but no team billing. Send them to the dashboard, which
    // already has the canonical "Setup incomplete" banner with the modal
    // wired to the Continue Setup / action-card CTAs. Rendering the
    // partner-targeted TeamAccountPromo ("Contact Sales") behind a modal
    // here would send the wrong cohort signal if the modal is dismissed.
    const hasTeamBilling = !!viewer.groups?.some((g: any) => !!g?.billing_account);
    if (viewer.id && viewer.signup_type === 'smb' && !hasTeamBilling) {
      return redirect('/dashboard');
    }

    if (!viewer.groups || viewer.groups.length === 0) {
      document.title = 'Upgrade your account';
      return (
        <Layout verticalCenter hideSearch showNewRequest={false}>
          <TeamAccountPromo />
        </Layout>
      );
    }

    const { stepPath } = params;

    if (!stepPath) {
      return redirect(`${baseUrl}/${steps[0].path}?${query.toString()}`);
    }

    const step = steps.find((s) => s.path === stepPath);
    if (!step) return <NotFoundPage />;
    const { component } = step;

    const fetches = component.fetch
      ? Array.isArray(component.fetch)
        ? component.fetch
        : [component.fetch]
      : [];

    for (const fetch of fetches) {
      await store.dispatch(fetch({ viewer }));
    }

    // if initial project_id is set, fetch the project so it can be preselected
    if (projectId) await store.dispatch(fetchProject(projectId));

    document.title = component.title;
    return <ExpertRequestNewConnected query={query} step={step} />;
  },
};

const PRIVATE_DISCLOSURE_ERROR =
  "GraphQL Error: You don't have permission to perform this action. Permission group#private_disclosure";
const PHONE_SAVE_ERROR = 'Error saving phone number.';
const CREATE_ERROR = 'Error when creating expert request.';

// Shared notify for create failures (used by the project-create and ER-create paths).
function notifyCreateError(err: any, notify: (message: string, level: string) => void) {
  if (err?.message?.startsWith(PRIVATE_DISCLOSURE_ERROR)) {
    notify('User is not authorized to choose private disclosure (premium).', 'error');
    return;
  }
  // ENG-5670. The api's raw message states the rule but not the alternative, so
  // this one refusal gets the coded copy instead. Deliberately scoped to the
  // single code rather than to any code that happens to have a default: the
  // branch below notifies EVERY error in a multi-error response, and a general
  // override would silently drop the rest. Falls through to the raw message if
  // the copy is ever removed.
  const coded =
    firstErrorCode(err) === ERROR_CODES.EXPERT_REQUEST_ER_TYPE_NOT_AVAILABLE
      ? getDefaultErrorMessage(ERROR_CODES.EXPERT_REQUEST_ER_TYPE_NOT_AVAILABLE)
      : undefined;
  if (coded) {
    notify(coded, 'error');
    return;
  }
  if (err?.rawError) {
    // Each error through apiErrorMessage's filter: saveProject reaches here too since
    // ENG-5322, and its refusals can carry permission-policy or unknown.unknown text.
    apiErrorMessages(err, CREATE_ERROR).forEach((message) => notify(message, 'error'));
    return;
  }
  notify(CREATE_ERROR, 'error');
  console.warn(err);
}

const connector = connect(
  (state: RootState) => ({
    viewer: state.viewer,
    projects: state.projects.names || [],
    groups: state.groups.all || [],
  }),
  {
    saveProject,
    invalidateFetchedProjectsCache,
    saveExpertRequest,
    notify,
    setAddress,
    popup,
  }
);

type ExpertRequestNewProps = {
  query: URLSearchParams;
  step: Step;
};

export const ExpertRequestNew = (
  props: ExpertRequestNewProps & ConnectedProps<typeof connector>
): JSX.Element => {
  const { viewer, query, popup, step } = props;

  const navigate = useNavigate();
  const graphql = useApolloClient();
  const [submitting, setSubmitting] = React.useState(false);
  const [pendingCreate, setPendingCreate] = React.useState<{
    values: any;
    projectId: string;
  } | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [initialValues, setInitialValues] = React.useState(() => {
    const initialProjectId = query.get('project_id');

    return getDefaultValues(viewer, initialProjectId);
  });

  const { LayoutParams } = step;
  const stepNumber = steps.indexOf(step);

  const onNext = useCallback(() => {
    const stepNumber = steps.indexOf(step);
    const nextUrl =
      steps[stepNumber + 1] && `${baseUrl}/${steps[stepNumber + 1].path}${queryPart(query)}`;
    navigate(nextUrl);
  }, [step, query, navigate]);

  useEffect(() => {
    const initialProjectId = query.get('project_id');
    const copyFrom = query.get('copy_from');
    if (copyFrom) {
      graphql.query({ query: GET_EXPERT_REQUEST, variables: { id: copyFrom } }).then(({ data }) => {
        const copyData = data.expertRequest;
        if (copyData) {
          // @ts-expect-error TS(2345) FIXME: Argument of type '{ formInitialized: true; copy_fr... Remove this comment to see the full error message
          setInitialValues({
            ...copyExpertRequest(viewer, copyData),
            formInitialized: true,
          });
        }
      });
    } else if (step.path === steps[0].path) {
      const defaultValues = getDefaultValues(viewer, initialProjectId);

      const cachedValue = getCache(cacheValueKey);
      const hasCachedValue = Boolean(cachedValue);

      const initDefaultValues = () => {
        const initialValues = defaultValues;
        if (defaultValues.project_id) {
          initialValues.project_id = defaultValues.project_id;
        }
        setInitialValues({ ...initialValues, formInitialized: true });
      };

      if (hasCachedValue) {
        popup({
          buttonAlignment: 'space-between',
          title: 'A previous Expert Request was found.',
          contents: 'Do you want to recover the previous expert request?',
          buttons: [
            {
              flat: true,
              label: 'No',
              callback: () => {
                clearCache(cacheValueKey);
                initDefaultValues();
              },
            },
            {
              label: 'Yes',
              callback: () => {
                cachedValue.currentVisit += 1;
                setInitialValues({ ...cachedValue, formInitialized: true });
                if (steps.indexOf(step) === 0) {
                  onNext();
                }
              },
            },
          ],
        });
      } else {
        initDefaultValues();
      }
    }
  }, [graphql, onNext, popup, query, step, viewer]);

  const handleSubmit = async (values: any, form: any) => {
    if (submitting) return;

    const { viewer, projects, saveProject, notify, setAddress } = props;

    if (!viewer.phone && values.phone) {
      try {
        // Awaited, or the catch below can never run (ENG-5322).
        await setAddress(viewer.profile?.id, 'phone', values.phone, true);
      } catch (err) {
        if (hasErrorCode(err, ERROR_CODES.ADDRESS_ADDRESS_TAKEN)) {
          // Returned, not passed to final-form's callback: an async onSubmit is
          // completed again with whatever its promise resolves to.
          return { phone: 'Already in use, please use another' };
        }
        const message = apiErrorMessage(err, PHONE_SAVE_ERROR);
        if (message === PHONE_SAVE_ERROR) Sentry.captureException(err);
        notify(message, 'error');
      }
    }

    const {
      project_name: projectName,
      tracking_code: trackingCode,
      ...expertRequestValues
    } = values;

    if (!expertRequestValues.name) {
      notify('Expert request must have a name.', 'error');
      return;
    }

    const groupId = values.group_id;
    let projectId = values.project_id;
    const projectEdges = (projects && projects.edges) || [];
    const shouldCreateProject =
      !projectId || !projectEdges.find((e: any) => e.node.id === projectId);

    setSubmitting(true);

    try {
      if (shouldCreateProject) {
        const createdProject = await saveProject({
          name: projectName || values.name,
          tracking_code: trackingCode,
          group_id: groupId,
        });
        projectId = createdProject.id;
        form.change('project_id', projectId);
        setCache(cacheValueKey, { ...values, project_id: projectId });
      }

      // ENG-5313: defer ER creation until the draft-vs-submit choice (ReadyToSourceModal).
      setPendingCreate({ values: expertRequestValues, projectId });
    } catch (err) {
      notifyCreateError(err, notify);
    } finally {
      setSubmitting(false);
    }
  };

  // Resolves the ReadyToSourceModal choice: create the ER at the chosen stage,
  // then continue into the invite-teammates step. Submit -> active (fires the
  // client sourcing email); Save as draft -> draft (no sourcing yet).
  const finishCreate = async (stage: ExpertRequestStage) => {
    if (!pendingCreate || creating) return;
    const { saveExpertRequest, invalidateFetchedProjectsCache, notify } = props;
    setCreating(true);
    try {
      const expertRequest = await saveExpertRequest(
        { ...pendingCreate.values, project_id: pendingCreate.projectId, stage },
        { fetchProject: true }
      );
      await invalidateFetchedProjectsCache();
      clearCache(cacheValueKey);
      segmentTracking('expert-request-ready-to-source', {
        choice: stage === ExpertRequestStage.Active ? 'submit' : 'draft',
      });
      navigate(`/expert_request/${expertRequest.id}/add_members`);
    } catch (err) {
      notifyCreateError(err, notify);
      setCreating(false);
    }
  };

  const renderStep = (isMobileVersion: any, { form, handleSubmit }: any) => {
    const { step } = props;
    const { component } = step;

    return React.createElement(component, {
      onNext: onNext,
      isMobileVersion,
      change: form.change,
      values: form.getState().values,
      handleSubmit,
    });
  };

  return (
    <Layout {...LayoutParams} hideSearch showNewRequest={false} selected="expert_requests">
      <Body style={{ paddingTop: 30, paddingBottom: 30 }}>
        <MediaQuery maxWidth={SCREEN_SM}>
          {(isMobileVersion: any) => (
            <Form
              onSubmit={handleSubmit}
              initialValues={initialValues}
              mutators={{
                ...arrayMutators,
              }}
              validate={(values) =>
                step &&
                step.component &&
                step.component.validate &&
                step.component.validate(values, props)
              }
            >
              {(formRenderProps) => (
                <>
                  <ExpertRequestSpy
                    cacheKey={cacheValueKey}
                    formValuesInitialized={initialValues.formInitialized && stepNumber > 1}
                  />
                  {renderStep(isMobileVersion, formRenderProps)}
                </>
              )}
            </Form>
          )}
        </MediaQuery>
        <ReadyToSourceModal
          open={pendingCreate !== null}
          submitting={creating}
          onSaveDraft={() => finishCreate(ExpertRequestStage.Draft)}
          onSubmitForSourcing={() => finishCreate(ExpertRequestStage.Active)}
          // Don't let a backdrop/escape dismiss drop the choice mid-create — the
          // create is already in flight and will still navigate on success.
          onClose={() => {
            if (!creating) setPendingCreate(null);
          }}
        />
      </Body>
    </Layout>
  );
};

const ExpertRequestNewConnected = connector(ExpertRequestNew);

export const addMembers: LegacyRoute = {
  path: `/expert_request/:id/add_members`,

  async action({ store, params }) {
    const id = params.id!;
    const expertRequest = await store.dispatch(fetchExpertRequest(id));
    if (!expertRequest) return <NotFoundPage />;

    return (
      <Layout hideSearch>
        <Helmet>
          <title>Invite Members</title>
        </Helmet>
        <Body style={{ paddingTop: 30, paddingBottom: 30 }}>
          <AddMembers
            project={expertRequest.project}
            expertRequestId={expertRequest.id}
            stage={expertRequest.stage}
          />
        </Body>
      </Layout>
    );
  },
};

export default route;
